/**
 * WorkbenchCanvas Component
 * Main center area for playlist editing, set operations, and sequence building
 */

import { useEffect, useMemo, useState } from 'react';
import { Boxes, Loader2 } from 'lucide-react';
import { useQueries } from '@tanstack/react-query';
import type { AudioFeatures } from '../../api/types';
import { useWorkbenchStore } from '../../stores/workbench';
import { useSequencerStore } from '../../stores/sequencer';
import { useSelectionStore } from '../../stores/selection';
import { useReorderPlaylist, useCanReorder } from '../../hooks/useReorderPlaylist';
import { toCamelot, getAllKeysWithinDistance } from '../../lib/camelot';
import type { SlotCriteria } from '../../stores/workbench';
import type { ReorderStrategyType } from '../../api/types';
import type { SmartActionType } from '../workbench/WorkbenchHeader';
import WorkbenchHeader from '../workbench/WorkbenchHeader';
import { TrackTable } from '../workbench/TrackTable';
import { SequencerOverlay } from '../workbench/SequencerOverlay';
import { FilterModal } from '../workbench/FilterModal';
import { usePlaylist } from '../../api/hooks/usePlaylists';
import { audioFeaturesService } from '../../api/services/tracks';
import { askAIService } from '../../api/services/askAI';
import { useUnsavedChangesWarning } from '../../hooks/useUnsavedChangesWarning';
import { toast } from '../ui/Toast';
import { notifyRateLimitedFromError } from '../../lib/rateLimitNotice';

export function WorkbenchCanvas() {
  const { activePlaylist, tracks, isDirty, addSmartSlot, addAISlot, capabilities } = useWorkbenchStore();
  const { isOpen: isSequencerOpen, open: openSequencer } = useSequencerStore();
  const { getSelectedIds } = useSelectionStore();
  const reorderMutation = useReorderPlaylist();
  const canReorder = useCanReorder();
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);

  // Warn user about unsaved changes when navigating away
  useUnsavedChangesWarning({
    when: isDirty,
    message: 'You have unsaved changes in your playlist. Do you want to leave without saving?',
  });

  // Fetch full playlist details when activePlaylist changes
  const { data: playlistData, isLoading: isPlaylistLoading } = usePlaylist(activePlaylist?.id);

  // Sync remote data to store when playlist loads
  useEffect(() => {
    if (playlistData && activePlaylist?.id === playlistData.id) {
      const isDirty = useWorkbenchStore.getState().isDirty;

      // Only update if not dirty (user hasn't edited)
      if (!isDirty) {
        // Use setActivePlaylist to ensure originalTracks is set correctly
        useWorkbenchStore.getState().setActivePlaylist(playlistData);
      }
    }
  }, [playlistData, activePlaylist?.id]);

  // Split track IDs into batches of 100 for parallel fetching
  const trackBatches = useMemo(() => {
    const trackIds = tracks.map(t => t.id);
    const batchSize = 100;
    const batches: string[][] = [];

    for (let i = 0; i < trackIds.length; i += batchSize) {
      batches.push(trackIds.slice(i, i + batchSize));
    }

    return batches;
  }, [tracks]);

  // Fetch audio features in parallel batches with React Query caching
  const audioFeatureQueries = useQueries({
    queries: trackBatches.map((batch) => ({
      queryKey: ['audioFeatures', 'batch', batch.join(',')],
      queryFn: () => audioFeaturesService.getBatch(batch),
      enabled: batch.length > 0,
      staleTime: 1000 * 60 * 5, // 5 minutes
    })),
  });

  // Combine results from all batches - updates progressively as each batch completes
  const audioFeaturesMap = useMemo(() => {
    const map = new Map<string, AudioFeatures>();

    for (const query of audioFeatureQueries) {
      if (query.data?.audioFeatures) {
        for (const af of query.data.audioFeatures) {
          map.set(af.id, af);
        }
      }
    }

    return map;
  }, [audioFeatureQueries]);

  // Handler for Smart Slot button
  const handleAddSmartSlot = () => {
    // Add smart slot after the last selected track, or at the end if no selection
    let position = tracks.length;
    const selectedIds = getSelectedIds();

    if (selectedIds.length > 0) {
      // Find the position of the last selected track
      const lastSelectedId = selectedIds[selectedIds.length - 1];
      const lastSelectedIndex = tracks.findIndex((t) => t.instanceId === lastSelectedId);
      if (lastSelectedIndex !== -1) {
        position = lastSelectedIndex + 1;
      }
    }

    // Get previous track for context (if exists)
    const prevTrack = position > 0 ? tracks[position - 1] : null;

    // Calculate default criteria based on previous track
    const criteria: SlotCriteria = {};

    if (prevTrack) {
      const features = audioFeaturesMap.get(prevTrack.id);

      if (features) {
        // BPM: ±15% from previous track
        if (features.tempo) {
          const tempoDelta = features.tempo * 0.15;
          criteria.bpm = {
            min: Math.round(features.tempo - tempoDelta),
            max: Math.round(features.tempo + tempoDelta),
          };
        }

        // Keys: Camelot-compatible keys (distance ≤ 3)
        if (features.key !== null && features.key !== undefined && features.mode !== null && features.mode !== undefined) {
          const prevCamelot = toCamelot(features.key, features.mode);
          criteria.keys = getAllKeysWithinDistance(prevCamelot, 3);
        }

        // Energy: ±0.2 from previous track
        if (features.energy !== null && features.energy !== undefined) {
          criteria.energy = {
            min: Math.max(0, features.energy - 0.2),
            max: Math.min(1, features.energy + 0.2),
          };
        }

        // Valence: ±0.2 from previous
        if (features.valence !== null && features.valence !== undefined) {
          criteria.valence = {
            min: Math.max(0, features.valence - 0.2),
            max: Math.min(1, features.valence + 0.2),
          };
        }

        // Danceability: ±0.2 from previous
        if (features.danceability !== null && features.danceability !== undefined) {
          criteria.danceability = {
            min: Math.max(0, features.danceability - 0.2),
            max: Math.min(1, features.danceability + 0.2),
          };
        }
      }
    }

    // If no previous track or no features, use reasonable defaults
    if (Object.keys(criteria).length === 0) {
      criteria.energy = { min: 0.4, max: 0.7 };
      criteria.bpm = { min: 100, max: 140 };
      criteria.valence = { min: 0.2, max: 0.9 };
      criteria.danceability = { min: 0.3, max: 0.9 };
    }

    addSmartSlot(position, criteria);
  };

  // Handler for Smart Action button (Ask AI or Smart Juggle)
  const handleSmartActionClick = async (action: SmartActionType) => {
    if (action === 'smart_juggle') {
      handleAddSmartSlot();
    } else if (action === 'ask_ai') {
      if (!activePlaylist) {
        toast.error('No active playlist');
        return;
      }

      // Determine position: after last selected track, or at the end
      let position = tracks.length;
      const selectedIds = getSelectedIds();

      if (selectedIds.length > 0) {
        const lastSelectedId = selectedIds[selectedIds.length - 1];
        const lastSelectedIndex = tracks.findIndex((t) => t.instanceId === lastSelectedId);
        if (lastSelectedIndex !== -1) {
          position = lastSelectedIndex + 1;
        }
      }

      // Show loading toast
      const loadingToastId = toast.loading('Asking AI for suggestions...');

      try {
        // Call backend API with 5 tracks
        const response = await askAIService.generateTracks({
          playlistId: activePlaylist.id,
          position: position,
          trackCount: 5,
          promptType: 'auto',
        });

        // Dismiss loading toast
        if (loadingToastId) {
          toast.dismiss(loadingToastId);
        }

        // Handle no tracks found
        if (response.matchStats.found === 0) {
          toast.error('No tracks found on Spotify. Try again.');
          return;
        }

        // Always add the tracks
        addAISlot(position, response.tracks, response.promptUsed);

        if (response.matchStats.found < response.matchStats.requested) {
          // Show warning
          toast.warning(
            `Only found ${response.matchStats.found}/${response.matchStats.requested} tracks`,
            { duration: 5000 }
          );
        } else {
          // All tracks found
          toast.success(`AI suggested ${response.tracks.length} tracks`);
        }
      } catch (error: any) {
        if (loadingToastId) {
          toast.dismiss(loadingToastId);
        }

        if (error.status === 429) {
          notifyRateLimitedFromError(error.details);
        } else if (error.status === 422) {
          toast.error('Could not parse AI response. Check console for details.');
          console.error('LLM parse error:', error);
        } else if (error.status === 500) {
          toast.error('AI service unavailable. Please try again later.');
        } else if (error.status === 404) {
          toast.error('Playlist not found.');
        } else {
          toast.error('Failed to generate suggestions. Please try again.');
          console.error('Ask AI error:', error);
        }
      }
    }
  };

  // Handler for Intelligent Order button
  const handleReorder = (strategy: ReorderStrategyType = 'full_dj_mode') => {
    if (!canReorder || !activePlaylist) return;

    // Determine start track: use selected track if exactly one is selected
    // Note: getSelectedIds() returns instanceIds (UUIDs), not Spotify track IDs
    const selectedInstanceIds = getSelectedIds();
    let startTrackId: string | null = null;

    if (selectedInstanceIds.length === 1) {
      // Find the track by instanceId and get its Spotify ID
      const selectedTrack = tracks.find(t => t.instanceId === selectedInstanceIds[0]);
      startTrackId = selectedTrack?.id ?? null;
    }

    reorderMutation.mutate({
      playlistId: activePlaylist.id,
      strategy,
      applyToSpotify: false, // Preview only, user can save manually
      startTrackId, // Use selected track's Spotify ID if one selected, otherwise automatic
    });
  };

  // Handler for Sequencer button
  const handleSequencerClick = () => {
    openSequencer();
  };

  // Handler for Filter button
  const handleFilterClick = () => {
    setIsFilterModalOpen(true);
  };

  return (
    <main className="flex-1 min-w-[500px] bg-zinc-950 flex flex-col overflow-hidden">
      {/* Dynamic Header - shows playlist info when active */}
      {activePlaylist && playlistData ? (
        <WorkbenchHeader
          playlist={playlistData}
          tracks={tracks}
          audioFeaturesMap={audioFeaturesMap}
          capabilities={capabilities}
          onFilterClick={handleFilterClick}
          onSmartActionClick={handleSmartActionClick}
          onIntelligentOrderClick={handleReorder}
          onSequencerClick={handleSequencerClick}
        />
      ) : activePlaylist ? (
        <div className="p-6 border-b border-zinc-800">
          <div className="flex items-center gap-2">
            <Loader2 className="w-6 h-6 text-emerald-500 animate-spin" />
            <span className="text-zinc-400">Loading playlist...</span>
          </div>
        </div>
      ) : (
        <div className="p-6 border-b border-zinc-800">
          <div className="flex items-center gap-2">
            <Boxes className="w-6 h-6 text-emerald-500" />
            <h2 className="text-xl font-semibold text-zinc-100">Workbench</h2>
          </div>
        </div>
      )}

      {/* Content Area */}
      {activePlaylist ? (
        <div className="flex-1 overflow-hidden relative">
          {isPlaylistLoading && tracks.length === 0 ? (
            <div className="absolute inset-0 flex items-center justify-center bg-zinc-950/50 z-10">
              <Loader2 className="w-8 h-8 text-emerald-500 animate-spin" />
            </div>
          ) : null}
          <TrackTable
            tracks={tracks}
            audioFeaturesMap={audioFeaturesMap}
          />
        </div>
      ) : (
        <div className="flex-1 flex flex-col items-center justify-center text-center p-8">
          <Boxes className="w-16 h-16 text-zinc-700 mb-4" />
          <h3 className="text-lg font-medium text-zinc-400 mb-2">
            Ready to Work
          </h3>
          <p className="text-sm text-zinc-500 max-w-md">
            Select a playlist from the library or create a new one to get started.
            Use Set Ops to combine playlists or Sequences to build radio-style programming.
          </p>
        </div>
      )}

      {/* Sequencer Overlay - conditionally rendered when open */}
      {isSequencerOpen && (
        <SequencerOverlay
          isOpen={isSequencerOpen}
          onClose={() => useSequencerStore.getState().close()}
        />
      )}

      {/* Filter Modal */}
      {activePlaylist && (
        <FilterModal
          isOpen={isFilterModalOpen}
          onClose={() => setIsFilterModalOpen(false)}
          onSuccess={() => {
            // Optional: Success callback
          }}
          audioFeaturesMap={audioFeaturesMap}
          trackIds={tracks.map((t) => t.id)}
        />
      )}
    </main>
  );
}
